import { useState, useEffect } from 'react';
import {
  Plus,
  Pencil,
  Trash2,
  Shield,
  ShieldCheck,
  Save,
  RefreshCw,
  UserCheck,
  UserX
} from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { toast } from 'sonner';
import adminApi from '@/lib/adminApi';

const UsersManager = ({ currentUser }) => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [userToDelete, setUserToDelete] = useState(null);
  const [saving, setSaving] = useState(false);

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    is_superadmin: false
  });

  const fetchUsers = async () => {
    try {
      const response = await adminApi.get(`/auth/users`);
      setUsers(response.data);
    } catch (error) {
      console.error('Error fetching users:', error);
      toast.error('Erreur lors du chargement des utilisateurs');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  const openAddDialog = () => {
    setEditingUser(null);
    setFormData({
      name: '',
      email: '',
      password: '',
      is_superadmin: false
    });
    setDialogOpen(true);
  };

  const openEditDialog = (user) => {
    setEditingUser(user);
    setFormData({
      name: user.name,
      email: user.email,
      password: '',
      is_superadmin: user.is_superadmin
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!formData.name.trim() || !formData.email.trim()) {
      toast.error('Le nom et l\'email sont requis');
      return;
    }
    if (!editingUser && !formData.password) {
      toast.error('Le mot de passe est requis');
      return;
    }

    setSaving(true);
    try {
      if (editingUser) {
        const updateData = {
          name: formData.name,
          email: formData.email,
          is_superadmin: formData.is_superadmin
        };
        if (formData.password) {
          updateData.password = formData.password;
        }
        await adminApi.put(`/auth/users/${editingUser.id}`, updateData);
        toast.success('Utilisateur mis à jour');
      } else {
        await adminApi.post(`/auth/users`, formData);
        toast.success('Utilisateur créé');
      }
      setDialogOpen(false);
      fetchUsers();
    } catch (error) {
      console.error('Error saving user:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'enregistrement');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!userToDelete) return;

    try {
      await adminApi.delete(`/auth/users/${userToDelete.id}`);
      toast.success('Utilisateur supprimé');
      setDeleteDialogOpen(false);
      setUserToDelete(null);
      fetchUsers();
    } catch (error) {
      console.error('Error deleting user:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de la suppression');
    }
  };

  const toggleActive = async (user) => {
    try {
      await adminApi.put(`/auth/users/${user.id}`, {
        is_active: !user.is_active
      });
      fetchUsers();
      toast.success(`Utilisateur ${user.is_active ? 'désactivé' : 'activé'}`);
    } catch (error) {
      console.error('Error toggling user:', error);
      toast.error('Erreur lors de la mise à jour');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <RefreshCw className="w-8 h-8 animate-spin text-[#1e3a8a]" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="users-manager">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="font-playfair text-2xl font-semibold text-[#0F172A]">
            Gestion des administrateurs
          </h2>
          <p className="text-slate-600 font-inter text-sm mt-1">
            {users.length} utilisateur(s)
          </p>
        </div>
        <Button
          onClick={openAddDialog}
          className="bg-[#1e3a8a] hover:bg-[#172554]"
          data-testid="add-user-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          Nouvel administrateur
        </Button>
      </div>

      {/* Users List */}
      <Card>
        <CardContent className="p-0">
          <div className="divide-y">
            {users.map((user) => (
              <div
                key={user.id}
                className="flex items-center gap-4 p-4 hover:bg-slate-50 transition-colors"
                data-testid={`user-row-${user.id}`}
              >
                <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                  user.is_superadmin ? 'bg-[#D4AF37]' : 'bg-[#1e3a8a]'
                }`}>
                  {user.is_superadmin ? (
                    <ShieldCheck className="w-5 h-5 text-white" />
                  ) : (
                    <Shield className="w-5 h-5 text-white" />
                  )}
                </div>
                
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-inter font-medium text-[#0F172A]">
                      {user.name}
                    </span>
                    {user.is_superadmin && (
                      <span className="text-xs px-2 py-0.5 rounded bg-[#D4AF37]/10 text-[#D4AF37]">
                        Super Admin
                      </span>
                    )}
                    {!user.is_active && (
                      <span className="text-xs px-2 py-0.5 rounded bg-red-100 text-red-600">
                        Désactivé
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-slate-500 font-inter">{user.email}</p>
                </div>

                <div className="flex items-center gap-2">
                  {user.id !== currentUser?.id && (
                    <>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => toggleActive(user)}
                        className={user.is_active ? 'text-green-600' : 'text-slate-400'}
                        data-testid={`toggle-user-${user.id}`}
                      >
                        {user.is_active ? <UserCheck size={18} /> : <UserX size={18} />}
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => openEditDialog(user)}
                        data-testid={`edit-user-${user.id}`}
                      >
                        <Pencil size={18} />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          setUserToDelete(user);
                          setDeleteDialogOpen(true);
                        }}
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        data-testid={`delete-user-${user.id}`}
                      >
                        <Trash2 size={18} />
                      </Button>
                    </>
                  )}
                  {user.id === currentUser?.id && (
                    <span className="text-xs text-slate-400 font-inter">Vous</span>
                  )}
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-md" data-testid="user-dialog">
          <DialogHeader>
            <DialogTitle className="font-playfair">
              {editingUser ? 'Modifier l\'administrateur' : 'Nouvel administrateur'}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div>
              <Label htmlFor="user-name" className="font-inter text-sm">Nom *</Label>
              <Input
                id="user-name"
                value={formData.name}
                onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                className="mt-2"
                placeholder="Nom complet"
                data-testid="user-name-input"
              />
            </div>

            <div>
              <Label htmlFor="user-email" className="font-inter text-sm">Email *</Label>
              <Input
                id="user-email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                className="mt-2"
                placeholder="admin@example.com"
                data-testid="user-email-input"
              />
            </div>

            <div>
              <Label htmlFor="user-password" className="font-inter text-sm">
                Mot de passe {editingUser ? '(laisser vide pour ne pas modifier)' : '*'}
              </Label>
              <Input
                id="user-password"
                type="password"
                value={formData.password}
                onChange={(e) => setFormData(prev => ({ ...prev, password: e.target.value }))}
                className="mt-2"
                placeholder="••••••••"
                data-testid="user-password-input"
              />
            </div>

            <div className="flex items-center justify-between pt-2">
              <div>
                <Label className="font-inter text-sm">Super Administrateur</Label>
                <p className="text-xs text-slate-500">Peut gérer les autres administrateurs</p>
              </div>
              <Switch
                checked={formData.is_superadmin}
                onCheckedChange={(checked) => setFormData(prev => ({ ...prev, is_superadmin: checked }))}
                data-testid="user-superadmin-switch"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>
              Annuler
            </Button>
            <Button
              onClick={handleSave}
              disabled={saving}
              className="bg-[#1e3a8a] hover:bg-[#172554]"
              data-testid="save-user-btn"
            >
              {saving ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
              Enregistrer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Supprimer cet administrateur ?</AlertDialogTitle>
            <AlertDialogDescription>
              Cette action est irréversible. L'utilisateur "{userToDelete?.name}" sera définitivement supprimé.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-red-600 hover:bg-red-700"
              data-testid="confirm-delete-user-btn"
            >
              Supprimer
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default UsersManager;
